import { Box, Chip, Divider, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useWeekViewContext } from "../../../contexts/WeekView/WeekViewContext";
import { useElementHeight } from "../../../hooks/useElementHeight";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { useScheduleWeeks } from "../../../hooks/useScheduleWeeks";
import { ChangesSource, type DayStats } from "../../../utils/models";
import { toIsoDate } from "../../../utils/scheduleWeeks";
import { formatCurrency, formatNumber, getChipColor } from "../../../utils/utilFunctions";
import { type CalendarDayDecoration, CustomCalendar } from "../../CustomCalendar/CustomCalendar";
import { CensusDisplay } from "../../staffingBlock/CensusDisplay";
import { holidayHeaderTint } from "../../team/Holidays/schedule/holidayTints";
import { ScheduleHolidayRibbon } from "../../team/Holidays/schedule/ScheduleHolidayRibbon";
import { usePageScheduleHolidays } from "../../team/Holidays/schedule/scheduleHolidaysContext";

type MobileStaffingHeaderProps = {
	date: DateTime;
	setDate: (date: DateTime) => void;
	onRootHeightChange?: (height: number) => void;
};

export const MobileStaffingHeader: FC<MobileStaffingHeaderProps> = ({ date, setDate, onRootHeightChange }) => {
	const { chosenDepartment, isHospital } = useHospitalData();
	const { viewerShifts } = useScheduleWeeks(date);
	const { weekViewData, firstDate, lastDate } = useWeekViewContext();
	const canViewWage = useRevealWage();
	const rootRef = useRef<HTMLDivElement>(null);
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const theme = useTheme();
	const { holidayOn } = usePageScheduleHolidays();

	// A holiday tints its day and puts a ribbon in the cell's corner, applied or not.
	const getHolidayDecoration = useCallback(
		(day: DateTime): CalendarDayDecoration | undefined => {
			const scheduleHoliday = holidayOn(day);
			if (scheduleHoliday === undefined) return undefined;
			return {
				background: holidayHeaderTint(theme),
				adornment: <ScheduleHolidayRibbon scheduleHoliday={scheduleHoliday} compact dataTestId={`mobile-week-holiday-ribbon-${day.toFormat("yyyy-MM-dd")}`} />,
			};
		},
		[holidayOn, theme],
	);

	// `date` comes from the URL, which is never validated against the schedule — so it can sit outside it
	// (deep link to an out-of-window date) or in a week that is still loading. Descendants that read the
	// day without a guard then throw on `undefined`; CensusDisplay -> EditCensusMenu did exactly that
	// and took the whole page down. The day is the shown week's, the same item the desktop Week view draws.
	const dayItem = useMemo(() => weekViewData.find((day) => toIsoDate(day.date) === toIsoDate(date)), [weekViewData, date]);
	const hasLoadedWeek = dayItem !== undefined;
	// A known range is what separates "this date isn't in the schedule" from "the schedule hasn't arrived
	// yet", so the snackbar stays silent during normal startup and refreshes.
	const isDateOutOfRange =
		firstDate !== undefined && lastDate !== undefined && (toIsoDate(date) < toIsoDate(firstDate) || toIsoDate(date) > toIsoDate(lastDate));

	const height = useElementHeight(rootRef);
	useEffect(() => {
		if (height > 0) onRootHeightChange?.(height);
	}, [height, onRootHeightChange]);

	// The day's scheduled stats as the week view computed them; a hospital shows none.
	const dayStats: DayStats | undefined = isHospital ? undefined : dayItem?.dayStats;

	const chipColor = useMemo(() => getChipColor(dayStats, chosenDepartment), [dayStats, chosenDepartment]);

	useEffect(() => {
		if (isDateOutOfRange) showSnackbar(t("staffing.date.out.of.range"), "error");
	}, [isDateOutOfRange, showSnackbar, t]);

	// Bail after every hook has run so hook order stays stable across the loaded/unloaded transition.
	if (!hasLoadedWeek) return null;

	return (
		<Box
			ref={rootRef}
			sx={{
				position: "sticky",
				top: 0,
				width: "100%",
				zIndex: 5,
				bgcolor: "white",
				boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
			}}
		>
			<CustomCalendar shifts={viewerShifts} value={date} onChange={setDate} showOneWeek={true} showTitle={true} getDayDecoration={getHolidayDecoration} />
			<Box>
				<Divider />

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-evenly",
						height: "42px",
						alignItems: "center",
					}}
				>
					<Chip
						label={<span style={{ color: chipColor.color }}>{dayStats ? formatNumber((dayStats.totalHours / dayStats.wantedHours) * 100, 1) : 0}%</span>}
						sx={{
							fontSize: "0.75rem",
							bgcolor: chipColor.backgroundColor,
							height: "20px",
							"& .MuiChip-label": {
								padding: "0 8px",
								lineHeight: "20px",
								color: chipColor.color,
							},
							mb: 0.5,
							gap: "10px",
						}}
					/>

					<CensusDisplay source={ChangesSource.Staffing} dayDate={date} displayTitle censusValue={dayStats?.census ?? 0} containerSx={{ gap: 1.2 }} />
				</Box>

				<Divider />

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-evenly",
						height: "42px",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}>Total Costs:</Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							{!canViewWage ? "$0.0" : dayStats && formatCurrency(dayStats.totalSpent)}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}> PPD: </Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							${!canViewWage ? "$0.0" : dayStats?.ppd}
						</Typography>
					</Box>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Typography sx={{ fontSize: "0.8rem", color: (theme) => alpha(theme.palette.primary.dark, 0.6) }}> HPPD: </Typography>
						<Typography
							sx={{
								fontWeight: "bold",
								fontSize: "0.8rem",
								color: "primary.dark",
							}}
						>
							{dayStats?.hppd}{" "}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
